import React, { useState, useEffect, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DraggableAttributes,
} from "@dnd-kit/core";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ActionCreator } from "easy-peasy";
import { match as Match } from "react-router-dom";

import ModalCreateUpdateCategory from "../../../category/create-update";
import ModalDuplicateCategory from "../../../category/duplicate";
import {
  EnumCategoryType,
  EnumPermissionEntity,
  EnumServiceType,
} from "../../../../../lib/ground-aws-graphql-core/api/graphql/types";
import { Category } from "../../../../../lib/ground-aws-graphql-core/models/Category";
import { Service } from "../../../../../lib/ground-aws-graphql-core/models/Service";
import AddElement from "../../../../../components/Tailwind/AddElement";
import { EnumAction } from "../../../../../utils/types";
import CategoryComponent from "../category";
import images from "../../../../../images";

type Props = {
  loading: boolean;
  serviceType: EnumServiceType;
  categories: Category[];
  services: Service[];
  handleEditService: (e: any, service: Service) => void;
  handleClickService: (e: any, service: Service) => void;
  fetchData: () => void;
  match: Match<{ cid: string }>;
  modalCreateUpdateCategoryIsOpen: boolean;
  setModalCreateUpdateCategoryIsOpen: ActionCreator<boolean>;
  modalDuplicateCategoryIsOpen: boolean;
  setModalDuplicateCategoryIsOpen: ActionCreator<boolean>;
  modalCategoryAction: EnumAction;
  modalCategorySelected: Category | null;
  handleEditCategory: (category: Category) => void;
  handleDuplicateCategory: (category: Category) => void;
  handleAddService: (category: Category) => void;
  handleOrderCategory: (
    id: string,
    buildingId: string,
    beforeId?: string,
    afterId?: string
  ) => void;
  handleOrderService: (
    id: string,
    categoryId: string,
    buildingId: string,
    beforeId?: string,
    afterId?: string
  ) => void;
};

type SortableCategoryProps = {
  category: Category;
  match: Match<{ cid: string }>;
  services: Service[];
  serviceType: EnumServiceType;
  handleEditService: (e: any, service: Service) => void;
  handleClickService: (e: any, service: Service) => void;
  fetchData: () => void;
  handleEditCategory: (category: Category) => void;
  handleDuplicateCategory: (category: Category) => void;
  handleAddService: (category: Category) => void;
  handleOrderService: (
    id: string,
    categoryId: string,
    buildingId: string,
    beforeId?: string,
    afterId?: string
  ) => void;
};

type DragHandleProps = {
  attributes: DraggableAttributes;
  listeners: SyntheticListenerMap | undefined;
};

const DragHandle = ({ attributes, listeners }: DragHandleProps) => (
  <div
    {...attributes}
    {...listeners}
    style={{ touchAction: "none" }}
    className="flex items-center"
    aria-roledescription="sortable"
  >
    <button
      type="button"
      aria-label="Déplacer"
      className="cursor-grab active:cursor-grabbing p-2 -ml-2"
    >
      <img src={images.drag} alt="⋮⋮" />
    </button>
    <div className="ml-2 text-sm text-gray-500 hidden md:block">
      Glisse pour réordonner
    </div>
  </div>
);

function SortableCategoryComponent({
  category,
  match,
  services,
  serviceType,
  handleEditService,
  handleClickService,
  fetchData,
  handleEditCategory,
  handleDuplicateCategory,
  handleAddService,
  handleOrderService,
}: SortableCategoryProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.9 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="mb-3">
      <div className="flex items-center mb-2">
        <DragHandle attributes={attributes} listeners={listeners} />
      </div>

      <CategoryComponent
        match={match}
        key={category.id}
        category={category}
        services={services.filter(
          (s: Service) => s.category?.id === category.id
        )}
        serviceType={serviceType}
        onEditService={handleEditService}
        onClickService={handleClickService}
        onEditCategory={handleEditCategory}
        onDuplicateCategory={handleDuplicateCategory}
        fetchData={fetchData}
        onAddService={handleAddService}
        onOrderService={handleOrderService}
      />
    </div>
  );
}

const SortableCategory = React.memo(SortableCategoryComponent);

const ListServiceCategories = ({
  serviceType,
  categories,
  services,
  handleEditService,
  handleClickService,
  fetchData,
  match,
  modalCreateUpdateCategoryIsOpen,
  setModalCreateUpdateCategoryIsOpen,
  modalDuplicateCategoryIsOpen,
  setModalDuplicateCategoryIsOpen,
  modalCategoryAction,
  modalCategorySelected,
  handleEditCategory,
  handleDuplicateCategory,
  handleAddService,
  handleOrderCategory,
  handleOrderService,
}: Props): JSX.Element => {
  const [items, setItems] = useState<Category[]>(categories);

  useEffect(() => setItems(categories), [categories]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const saveCategoryOrder = useCallback(async (id, ids: string[]) => {
    const newIndex = ids.findIndex(cId => cId === id);

    const beforeId = newIndex > 0 ? ids[newIndex - 1] : undefined;
    const afterId = newIndex < ids.length - 1 ? ids[newIndex + 1] : undefined;
    handleOrderCategory(id, match.params.cid, beforeId, afterId);
  }, []);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setItems(prev => {
      const oldIndex = prev.findIndex(c => c.id === active.id);
      const newIndex = prev.findIndex(c => c.id === over.id);
      const newOrder = arrayMove(prev, oldIndex, newIndex);

      saveCategoryOrder(
        active.id,
        newOrder.map(c => c.id)
      );

      return newOrder;
    });
  };

  const handleToggleModalCreateUpdateCategory = () => {
    setModalCreateUpdateCategoryIsOpen(!modalCreateUpdateCategoryIsOpen);
  };

  const handleToggleModalDuplicateCategory = () => {
    setModalDuplicateCategoryIsOpen(!modalDuplicateCategoryIsOpen);
  };

  return (
    <>
      {items && items.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map(c => c.id)}
            strategy={verticalListSortingStrategy}
          >
            {items.map(category => (
              <SortableCategory
                key={category.id}
                category={category}
                match={match}
                services={services}
                serviceType={serviceType}
                handleEditService={handleEditService}
                handleClickService={handleClickService}
                fetchData={fetchData}
                handleEditCategory={handleEditCategory}
                handleDuplicateCategory={handleDuplicateCategory}
                handleAddService={handleAddService}
                handleOrderService={handleOrderService}
              />
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        <AddElement
          onClick={handleToggleModalCreateUpdateCategory}
          entity={EnumPermissionEntity.CATEGORY}
        />
      )}

      <ModalCreateUpdateCategory
        isOpen={modalCreateUpdateCategoryIsOpen}
        parent={false}
        categories={items}
        toggle={handleToggleModalCreateUpdateCategory}
        action={modalCategoryAction}
        type={
          serviceType === EnumServiceType.SPACE
            ? EnumCategoryType.SPACE
            : EnumCategoryType.SERVICE
        }
        category={modalCategorySelected}
        centerId={match.params.cid}
      />

      <ModalDuplicateCategory
        isOpen={modalDuplicateCategoryIsOpen}
        toggle={handleToggleModalDuplicateCategory}
        serviceType={serviceType}
        type={
          serviceType === EnumServiceType.SPACE
            ? EnumCategoryType.SPACE
            : EnumCategoryType.SERVICE
        }
        category={modalCategorySelected}
        match={match}
      />
    </>
  );
};

export default ListServiceCategories;
